import React, { useState, useEffect } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Paper, Avatar, useMediaQuery, alpha, IconButton,
  SwipeableDrawer, List, ListItem, ListItemText, ListItemIcon,
  Divider, BottomNavigation, BottomNavigationAction
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  People as PeopleIcon,
  Computer as ComputerIcon,
  Assignment as OsIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Print as PrintIcon,
  TrendingUp as TrendingUpIcon,
  Build as BuildIcon,
  Pending as PendingIcon,
  Dashboard as DashboardIcon,
  ListAlt as ListAltIcon,
  BarChart as BarChartIcon,
  Close as CloseIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

// Card de Estatística Mobile
function StatsCard({ title, value, icon, color, subtitle, highlight, onClick }) {
  const colors = {
    primary: '#0A2647',
    success: '#2ecc71',
    warning: '#f39c12',
    danger: '#e74c3c',
    info: '#3498db',
    purple: '#9b59b6',
    orange: '#ff6b00'
  };

  const selectedColor = colors[color] || colors.primary;

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <Card sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 3,
        background: highlight 
          ? `linear-gradient(135deg, ${selectedColor} 0%, ${selectedColor}CC 100%)`
          : `linear-gradient(135deg, ${selectedColor}08 0%, ${selectedColor}03 100%)`,
        border: highlight 
          ? 'none'
          : `1px solid ${alpha(selectedColor, 0.1)}`,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        color: highlight ? 'white' : 'inherit',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -30,
          right: -30,
          width: { xs: 80, sm: 120 },
          height: { xs: 80, sm: 120 },
          background: highlight
            ? 'rgba(255,255,255,0.08)'
            : `radial-gradient(circle, ${alpha(selectedColor, 0.05)} 0%, transparent 70%)`,
          borderRadius: '50%'
        },
        '&:active': {
          transform: 'scale(0.97)',
          transition: 'transform 0.1s ease'
        }
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
            <Typography variant="caption" sx={{ 
              color: highlight ? 'rgba(255,255,255,0.8)' : 'text.secondary',
              fontWeight: 500,
              display: 'block',
              mb: 0.5,
              fontSize: { xs: '0.65rem', sm: '0.75rem' }
            }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              color: highlight ? 'white' : selectedColor,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ 
                color: highlight ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                display: 'block',
                mt: 0.5,
                fontSize: { xs: '0.6rem', sm: '0.75rem' }
              }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            bgcolor: highlight 
              ? 'rgba(255,255,255,0.15)'
              : alpha(selectedColor, 0.1),
            borderRadius: '50%',
            p: { xs: 1, sm: 1.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            {React.cloneElement(icon, { 
              sx: { 
                color: highlight ? 'white' : selectedColor,
                fontSize: { xs: 20, sm: 28 }
              }
            })}
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
}

// Drawer Mobile para detalhes
function MobileDetailDrawer({ open, onClose, title, children }) {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '85vh',
          p: 2
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        pb: 1,
        borderBottom: '1px solid rgba(0,0,0,0.06)'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      {children}
    </SwipeableDrawer>
  );
}

// Componente de OS Mobile
function OsMobile({ os, onPrint }) {
  const getStatusColor = (status) => {
    const colors = {
      aberta: 'warning',
      em_andamento: 'info',
      finalizada: 'success',
      cancelada: 'error',
      pendente: 'warning',
      aprovada: 'success',
      orcamento: 'warning'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      aberta: 'Aberta',
      em_andamento: 'Em Andamento',
      finalizada: 'Finalizada',
      cancelada: 'Cancelada',
      pendente: 'Pendente',
      aprovada: 'Aprovada',
      orcamento: 'Orçamento'
    };
    return labels[status?.toLowerCase()] || status;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <Card sx={{ 
      mb: 1.5, 
      borderRadius: 2,
      border: '1px solid rgba(0,0,0,0.06)',
      '&:active': { transform: 'scale(0.98)' }
    }}>
      <Box sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
              #{os.numero_os}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {os.cliente_nome}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(os.status)}
            size="small"
            color={getStatusColor(os.status)}
            sx={{ borderRadius: 2, fontWeight: 500 }}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(os.data_abertura), 'dd/MM/yyyy')}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
              {formatCurrency(os.valor_total)}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => onPrint(os.id)}
              sx={{ color: '#0A2647' }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

// Lista de OS Mobile - APENAS 5
function OsListMobile({ osList, onPrint, loading }) {
  const ultimas5 = osList.slice(0, 5);

  if (loading) {
    return <LinearProgress sx={{ borderRadius: 2, height: 4 }} />;
  }

  if (ultimas5.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">Nenhuma OS encontrada</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {ultimas5.map((os) => (
        <OsMobile key={os.id} os={os} onPrint={onPrint} />
      ))}
    </Box>
  );
}

// Versão Desktop da Lista de OS - APENAS 5
function OsListDesktop({ osList, onPrint, loading }) {
  const ultimas5 = osList.slice(0, 5);
  
  const getStatusColor = (status) => {
    const colors = {
      aberta: 'warning',
      em_andamento: 'info',
      finalizada: 'success',
      cancelada: 'error',
      pendente: 'warning',
      aprovada: 'success',
      orcamento: 'warning'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      aberta: 'Aberta',
      em_andamento: 'Em Andamento',
      finalizada: 'Finalizada',
      cancelada: 'Cancelada',
      pendente: 'Pendente',
      aprovada: 'Aprovada',
      orcamento: 'Orçamento'
    };
    return labels[status?.toLowerCase()] || status;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3, p: 2 }}>
        <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 2.5, 
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ color: '#0A2647', fontWeight: 600 }}>
          📋 Últimas 5 Ordens de Serviço
        </Typography>
        <Chip 
          label={`${ultimas5.length} registros`}
          size="small"
          sx={{ 
            bgcolor: alpha('#0A2647', 0.08),
            color: '#0A2647',
            fontWeight: 500,
            borderRadius: 2
          }}
        />
      </Box>
      
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha('#0A2647', 0.04) }}>
              <TableCell><strong>OS</strong></TableCell>
              <TableCell><strong>Cliente</strong></TableCell>
              <TableCell><strong>Data</strong></TableCell>
              <TableCell align="right"><strong>Valor</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ultimas5.map((os) => (
              <TableRow key={os.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                    #{os.numero_os}
                  </Typography>
                </TableCell>
                <TableCell>{os.cliente_nome}</TableCell>
                <TableCell>
                  {format(new Date(os.data_abertura), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                    {formatCurrency(os.valor_total)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(os.status)}
                    size="small"
                    color={getStatusColor(os.status)}
                    sx={{ borderRadius: 2, fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    onClick={() => onPrint(os.id)}
                    sx={{ 
                      color: '#0A2647',
                      '&:hover': { bgcolor: alpha('#0A2647', 0.08) }
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {ultimas5.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nenhuma ordem de serviço encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [osList, setOsList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState('');
  const [bottomNav, setBottomNav] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    resumo: { total_clientes: 0, total_computadores: 0, os_mes: 0, faturamento_mes: 0 },
    os_por_status: [],
    os_por_mes: [],
    faturamento_por_mes: []
  });

  useEffect(() => {
    carregarDashboard();
    carregarOS();
  }, []);

  const carregarDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard');
      const data = response.data || {};
      setDashboardData({
        resumo: data.resumo || { total_clientes: 0, total_computadores: 0, os_mes: 0, faturamento_mes: 0 },
        os_por_status: Array.isArray(data.os_por_status) ? data.os_por_status : [],
        os_por_mes: Array.isArray(data.os_por_mes) ? data.os_por_mes : [],
        faturamento_por_mes: Array.isArray(data.faturamento_por_mes) ? data.faturamento_por_mes : []
      });
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const carregarOS = async () => {
    try {
      const response = await api.get('/os?limit=5');
      setOsList(response.data.ordens || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar OS:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getOsCountByStatus = (status) => {
    const item = dashboardData.os_por_status.find(
      s => s.status?.toLowerCase() === status.toLowerCase()
    );
    return item?.total || 0;
  };

  const totalOsAbertas = 
    getOsCountByStatus('aberta') + 
    getOsCountByStatus('em_andamento') + 
    getOsCountByStatus('pendente');

  const handlePrint = (id) => {
    const token = localStorage.getItem('@Jobson:token');
    window.open(`/api/os/${id}/print?token=${token}`, '_blank');
  };

  const handleCardClick = (type) => {
    if (isMobile) {
      setDrawerContent(type);
      setDrawerOpen(true);
    }
  };

  const statusChartData = {
    labels: dashboardData.os_por_status.map(item => {
      const labels = {
        aberta: 'Aberta',
        em_andamento: 'Em Andamento',
        finalizada: 'Finalizada',
        cancelada: 'Cancelada',
        pendente: 'Pendente',
        aprovada: 'Aprovada',
        orcamento: 'Orçamento'
      };
      return labels[item.status?.toLowerCase()] || item.status;
    }),
    datasets: [{
      data: dashboardData.os_por_status.map(item => item.total),
      backgroundColor: ['#ff9800', '#2196f3', '#4caf50', '#f44336', '#9e9e9e', '#8bc34a', '#ff5722'],
      borderWidth: 0,
    }],
  };

  const osMesChartData = {
    labels: dashboardData.os_por_mes.map(item => item.mes),
    datasets: [{
      label: 'OS por Mês',
      data: dashboardData.os_por_mes.map(item => item.total),
      borderColor: '#0A2647',
      backgroundColor: alpha('#0A2647', 0.05),
      tension: 0.4,
      fill: true,
    }],
  };

  // Gráfico de Faturamento Mês a Mês
  const faturamentoChartData = {
    labels: dashboardData.faturamento_por_mes.map(item => item.mes),
    datasets: [{
      label: 'Faturamento',
      data: dashboardData.faturamento_por_mes.map(item => item.total),
      backgroundColor: ['#2ecc71', '#27ae60', '#1abc9c', '#16a085', '#3498db', '#2980b9'],
      borderRadius: 8,
      borderSkipped: false,
    }],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(10,38,71,0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 10,
        padding: 12,
        callbacks: {
          label: function(context) {
            return formatCurrency(context.parsed.y);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(10,38,71,0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 10,
        padding: 12
      }
    },
  };

  if (loading) {
    return (
      <Layout title="Dashboard">
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha('#0A2647', 0.1),
            '& .MuiLinearProgress-bar': { 
              backgroundColor: '#0A2647',
              borderRadius: 4
            }
          }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">
      <Box sx={{ flexGrow: 1, pb: isMobile ? 8 : 0 }}>
        {/* Cards de Resumo */}
        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatsCard
              title="Clientes"
              value={dashboardData.resumo.total_clientes}
              icon={<PeopleIcon />}
              color="primary"
              subtitle="Total cadastrados"
              onClick={() => handleCardClick('clientes')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatsCard
              title="OS Abertas"
              value={totalOsAbertas}
              icon={<PendingIcon />}
              color="warning"
              subtitle="Aguardando atendimento"
              highlight={totalOsAbertas > 0}
              onClick={() => handleCardClick('os_abertas')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatsCard
              title="Em Andamento"
              value={getOsCountByStatus('em_andamento')}
              icon={<BuildIcon />}
              color="info"
              subtitle="Em execução"
              onClick={() => handleCardClick('os_andamento')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatsCard
              title="Faturamento"
              value={formatCurrency(dashboardData.resumo.faturamento_mes)}
              icon={<MoneyIcon />}
              color="success"
              subtitle={`${dashboardData.resumo.os_mes} OS`}
              onClick={() => handleCardClick('faturamento')}
            />
          </Grid>
        </Grid>

        {/* Conteúdo principal - Responsivo */}
        {isMobile ? (
          // VERSÃO MOBILE
          <Box>
            {/* Gráfico de Faturamento Mês a Mês - Mobile */}
            {dashboardData.faturamento_por_mes.length > 0 && (
              <Card sx={{ borderRadius: 3, p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#0A2647', fontWeight: 600 }}>
                  💰 Faturamento Mês a Mês
                </Typography>
                <Box sx={{ height: 200 }}>
                  <Bar data={faturamentoChartData} options={barChartOptions} />
                </Box>
              </Card>
            )}

            {/* Gráficos em cards menores */}
            {dashboardData.os_por_status.length > 0 && (
              <Card sx={{ borderRadius: 3, p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#0A2647', fontWeight: 600 }}>
                  📊 Status das OS
                </Typography>
                <Box sx={{ height: 180, display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: '60%', maxWidth: 180 }}>
                    <Doughnut data={statusChartData} options={chartOptions} />
                  </Box>
                </Box>
              </Card>
            )}

            {dashboardData.os_por_mes.length > 0 && (
              <Card sx={{ borderRadius: 3, p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#0A2647', fontWeight: 600 }}>
                  📈 Tendência de OS
                </Typography>
                <Box sx={{ height: 180 }}>
                  <Line data={osMesChartData} options={chartOptions} />
                </Box>
              </Card>
            )}

            {/* Lista de OS Mobile - APENAS 5 */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6" sx={{ color: '#0A2647', fontWeight: 600 }}>
                  📋 Últimas 5 OS
                </Typography>
                <Chip 
                  label={`${osList.length}`}
                  size="small"
                  sx={{ 
                    bgcolor: alpha('#0A2647', 0.08),
                    color: '#0A2647',
                    fontWeight: 500,
                    borderRadius: 2
                  }}
                />
              </Box>
              <OsListMobile osList={osList} onPrint={handlePrint} loading={loading} />
            </Box>
          </Box>
        ) : (
          // VERSÃO DESKTOP
          <>
            {/* Gráfico de Faturamento Mês a Mês - Desktop */}
            {dashboardData.faturamento_por_mes.length > 0 && (
              <Card sx={{ borderRadius: 3, p: 2.5, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#0A2647', fontWeight: 600 }}>
                  💰 Faturamento Mês a Mês
                </Typography>
                <Box sx={{ height: 250 }}>
                  <Bar data={faturamentoChartData} options={barChartOptions} />
                </Box>
              </Card>
            )}

            {/* Gráficos Desktop */}
            {dashboardData.os_por_status.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Card sx={{ borderRadius: 3, p: 2.5 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#0A2647', fontWeight: 600 }}>
                        📊 Status das OS
                      </Typography>
                      <Box sx={{ height: 220, display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ width: '70%', maxWidth: 220 }}>
                          <Doughnut data={statusChartData} options={chartOptions} />
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>

                {dashboardData.os_por_mes.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <motion.div
                      initial={{ x: 20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Card sx={{ borderRadius: 3, p: 2.5 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#0A2647', fontWeight: 600 }}>
                          📈 Tendência de OS
                        </Typography>
                        <Box sx={{ height: 220 }}>
                          <Line data={osMesChartData} options={chartOptions} />
                        </Box>
                      </Card>
                    </motion.div>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Lista de OS Desktop - APENAS 5 */}
            <OsListDesktop osList={osList} onPrint={handlePrint} loading={loading} />
          </>
        )}

        {/* Mobile Detail Drawer */}
        <MobileDetailDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={
            drawerContent === 'clientes' ? '👥 Clientes' :
            drawerContent === 'os_abertas' ? '📋 OS Abertas' :
            drawerContent === 'os_andamento' ? '🔧 Em Andamento' :
            drawerContent === 'faturamento' ? '💰 Faturamento' : 'Detalhes'
          }
        >
          <Box sx={{ py: 2 }}>
            {drawerContent === 'clientes' && (
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#0A2647', textAlign: 'center' }}>
                  {dashboardData.resumo.total_clientes}
                </Typography>
                <Typography color="text.secondary" align="center">
                  Total de clientes cadastrados
                </Typography>
              </Box>
            )}
            {drawerContent === 'os_abertas' && (
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#f39c12', textAlign: 'center' }}>
                  {totalOsAbertas}
                </Typography>
                <Typography color="text.secondary" align="center">
                  OS aguardando atendimento
                </Typography>
                {totalOsAbertas === 0 && (
                  <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: '#4caf50' }}>
                    ✅ Nenhuma OS pendente! Tudo em dia!
                  </Typography>
                )}
              </Box>
            )}
            {drawerContent === 'os_andamento' && (
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#2196f3', textAlign: 'center' }}>
                  {getOsCountByStatus('em_andamento')}
                </Typography>
                <Typography color="text.secondary" align="center">
                  OS em execução no momento
                </Typography>
              </Box>
            )}
            {drawerContent === 'faturamento' && (
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#2ecc71', textAlign: 'center' }}>
                  {formatCurrency(dashboardData.resumo.faturamento_mes)}
                </Typography>
                <Typography color="text.secondary" align="center">
                  Faturamento do mês
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  {dashboardData.resumo.os_mes} OS finalizadas
                </Typography>
              </Box>
            )}
          </Box>
        </MobileDetailDrawer>
      </Box>
    </Layout>
  );
}

export default Dashboard;
